import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Loader2, Check, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { skillsList } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Onboarding = () => {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Get token from localStorage as fallback
  const getAuthToken = () => {
    return token || localStorage.getItem('token');
  };
  
  // Step 1: Resume
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  
  // Step 2: Profile
  const [profile, setProfile] = useState({
    title: '',
    bio: '',
    skills: [],
    experience_years: '',
    preferred_categories: [],
    hourly_rate_min: '',
    hourly_rate_max: '',
    location: '',
    linkedin_url: '',
    github_url: '',
  });
  const [skillInput, setSkillInput] = useState('');
  
  // Step 3: Interview
  const [interviewQuestions, setInterviewQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [interviewComplete, setInterviewComplete] = useState(false);
  
  // Step 4: Results
  const [matchedRoles, setMatchedRoles] = useState([]);

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const categories = [
    { id: 'ai', name: 'AI & ML' },
    { id: 'tech', name: 'Software' },
    { id: 'finance', name: 'Finance' },
    { id: 'healthcare', name: 'Healthcare' },
    { id: 'general', name: 'General' },
  ];

  // Handle resume upload
  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.match(/\.(pdf|doc|docx|txt)$/i)) {
      toast({ title: 'Invalid file type', description: 'Please upload a PDF, DOC, DOCX, or TXT file', variant: 'destructive' });
      return;
    }

    setResumeFile(file);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/resume/upload`, formData, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setParsedData(response.data.parsed_data);
      setResumeUploaded(true);
      
      // Pre-fill profile with parsed data
      if (response.data.parsed_data) {
        const parsed = response.data.parsed_data;
        setProfile(prev => ({
          ...prev,
          title: parsed.title || prev.title,
          bio: parsed.summary || prev.bio,
          skills: parsed.skills || prev.skills,
          experience_years: parsed.experience_years || prev.experience_years,
        }));
      }
      
      toast({ title: 'Resume uploaded!', description: 'We\'ve extracted your information.' });
    } catch (error) {
      toast({ title: 'Upload failed', description: error.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Handle profile save
  const handleSaveProfile = async () => {
    if (!profile.title || profile.skills.length === 0) {
      toast({ title: 'Please fill required fields', description: 'Title and at least one skill are required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API}/profile`, profile, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      updateUser(response.data);
      
      // Fetch interview questions
      const questionsRes = await axios.post(`${API}/interview/start`, {}, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setInterviewQuestions(questionsRes.data.questions);
      
      setStep(3);
      toast({ title: 'Profile saved!', description: 'Now let\'s do a quick interview.' });
    } catch (error) {
      toast({ title: 'Failed to save profile', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Handle interview answer
  const handleAnswerSubmit = async () => {
    const currentQuestion = interviewQuestions[currentQuestionIndex];
    const answer = answers[currentQuestion.id];

    if (!answer || answer.trim().length < 20) {
      toast({ title: 'Please provide a more detailed answer', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/interview/answer`, {
        question_id: currentQuestion.id,
        answer: answer,
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (currentQuestionIndex < interviewQuestions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        // Complete interview and get matches
        const matchRes = await axios.post(`${API}/interview/complete`, {}, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        setMatchedRoles(matchRes.data.matched_roles);
        setInterviewComplete(true);
        setStep(4);
        toast({ title: 'Interview complete!', description: 'Check out your matched roles.' });
      }
    } catch (error) {
      toast({ title: 'Failed to submit answer', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Complete onboarding
  const handleCompleteOnboarding = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/complete`, {}, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      updateUser(response.data);
      toast({ title: 'Welcome aboard!', description: 'Your profile is ready.' });
      navigate('/dashboard');
    } catch (error) {
      toast({ title: 'Something went wrong', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Add skill
  const addSkill = (skill) => {
    if (skill && !profile.skills.includes(skill)) {
      setProfile(prev => ({ ...prev, skills: [...prev.skills, skill] }));
    }
    setSkillInput('');
  };

  // Remove skill
  const removeSkill = (skill) => {
    setProfile(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  // Toggle category
  const toggleCategory = (categoryId) => {
    setProfile(prev => ({
      ...prev,
      preferred_categories: prev.preferred_categories.includes(categoryId)
        ? prev.preferred_categories.filter(c => c !== categoryId)
        : [...prev.preferred_categories, categoryId],
    }));
  };

  const filteredSkills = skillsList.filter(
    s => s.toLowerCase().includes(skillInput.toLowerCase()) && !profile.skills.includes(s)
  ).slice(0, 5);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-gray-900">Complete Your Profile</h1>
            <span className="text-sm text-gray-500">Step {step} of {totalSteps}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Step 1: Resume Upload */}
        {step === 1 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Upload Your Resume</h2>
            <p className="text-gray-500 mb-8">We'll use AI to extract your skills and experience.</p>

            {!resumeUploaded ? (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl p-12 cursor-pointer hover:border-indigo-300 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleResumeUpload}
                  className="hidden"
                />
                {loading ? (
                  <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
                ) : (
                  <Upload className="w-12 h-12 text-gray-400 mb-4" />
                )}
                <span className="font-medium text-gray-700">Click to upload or drag and drop</span>
                <span className="text-sm text-gray-400 mt-1">PDF, DOC, DOCX, or TXT (max 10MB)</span>
              </label>
            ) : (
              <div className="border border-green-200 bg-green-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{resumeFile?.name}</p>
                    <p className="text-sm text-green-600">Successfully parsed</p>
                  </div>
                </div>
                {parsedData && (
                  <div className="space-y-2 text-sm text-gray-600">
                    {parsedData.title && <p><strong>Title:</strong> {parsedData.title}</p>}
                    {parsedData.skills?.length > 0 && (
                      <p><strong>Skills found:</strong> {parsedData.skills.slice(0, 5).join(', ')}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!resumeUploaded}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Profile Details */}
        {step === 2 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Complete Your Profile</h2>
            <p className="text-gray-500 mb-8">Tell us more about yourself and your preferences.</p>

            <div className="space-y-6">
              <div>
                <Label htmlFor="title">Professional Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Senior Software Engineer"
                  value={profile.title}
                  onChange={(e) => setProfile(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  placeholder="Brief summary about your experience and expertise..."
                  value={profile.bio}
                  onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                  className="mt-1.5 h-24"
                />
              </div>

              <div>
                <Label>Skills *</Label>
                <div className="flex flex-wrap gap-2 mb-2 mt-1.5">
                  {profile.skills.map(skill => (
                    <Badge key={skill} variant="secondary" className="gap-1">
                      {skill}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeSkill(skill)} />
                    </Badge>
                  ))}
                </div>
                <div className="relative">
                  <Input
                    placeholder="Type to search skills..."
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addSkill(skillInput)}
                  />
                  {skillInput && filteredSkills.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border rounded-lg mt-1 shadow-lg">
                      {filteredSkills.map(skill => (
                        <div
                          key={skill}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-50"
                          onClick={() => addSkill(skill)}
                        >
                          {skill}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label>Preferred Categories</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {categories.map(cat => (
                    <Badge
                      key={cat.id}
                      variant={profile.preferred_categories.includes(cat.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleCategory(cat.id)}
                    >
                      {cat.name}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    placeholder="50"
                    value={profile.hourly_rate_min}
                    onChange={(e) => setProfile(prev => ({ ...prev, hourly_rate_min: e.target.value }))}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Max Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    placeholder="150"
                    value={profile.hourly_rate_max}
                    onChange={(e) => setProfile(prev => ({ ...prev, hourly_rate_max: e.target.value }))}
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  placeholder="5"
                  value={profile.experience_years}
                  onChange={(e) => setProfile(prev => ({ ...prev, experience_years: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={handleSaveProfile}
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Continue to Interview <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: AI Interview */}
        {step === 3 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">AI Interview</h2>
            <p className="text-gray-500 mb-8">
              Answer a few questions to help us understand your expertise.
              Question {currentQuestionIndex + 1} of {interviewQuestions.length}
            </p>

            {interviewQuestions.length > 0 && (
              <div>
                <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                  <p className="text-indigo-900 font-medium">
                    {interviewQuestions[currentQuestionIndex]?.question}
                  </p>
                </div>

                <Textarea
                  placeholder="Type your answer here... (minimum 20 characters)"
                  value={answers[interviewQuestions[currentQuestionIndex]?.id] || ''}
                  onChange={(e) => setAnswers(prev => ({
                    ...prev,
                    [interviewQuestions[currentQuestionIndex]?.id]: e.target.value
                  }))}
                  className="h-40 mb-4"
                />

                <div className="flex justify-between">
                  <Button 
                    variant="outline" 
                    onClick={() => currentQuestionIndex > 0 && setCurrentQuestionIndex(prev => prev - 1)}
                    disabled={currentQuestionIndex === 0}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                  <Button 
                    onClick={handleAnswerSubmit}
                    disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {currentQuestionIndex < interviewQuestions.length - 1 ? 'Next Question' : 'Complete Interview'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && (
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">You're All Set!</h2>
              <p className="text-gray-500">Based on your profile, here are some roles that match your expertise.</p>
            </div>

            <div className="space-y-4 mb-8">
              {matchedRoles.slice(0, 5).map((role, index) => (
                <div key={role.id || index} className="border rounded-xl p-4 hover:border-indigo-200 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{role.title}</h3>
                      <p className="text-sm text-gray-500">{role.company}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-indigo-600 font-semibold">{role.payRange || role.pay_range}</p>
                      <p className="text-xs text-gray-400">{role.match_score ? `${role.match_score}% match` : role.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button 
              onClick={handleCompleteOnboarding}
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
